import React from 'react';
import _ from 'lodash';
import { styled } from '@this/constants/themes';
import { media } from '@this/components/shared/atoms/media';
import type Trip from '@this/src/domain/trip/trip2';
import type Applicant from '@this/src/domain/trip/applicant';
import type Department from '@this/domain/department/department2';
import ApprovalAuthority from '@this/domain/approval_authority/approval_authority';
import Approver from '@this/domain/approver/approver';
import { reportError } from '@this/src/lib/bugsnag';

interface Props {
  trip: Trip;
  applicant?: Applicant;
  currentStatus: number | null;
}

interface State {
  trip: Trip;
  department: Department | null;
  statusList: { name: string; current: boolean; approvers: string[] }[];
  currentStatus: number | null;
}

export type ApprovalStatusItem = { name: string; current: boolean; approvers: string[] };

export function computeApprovalBadgeStates(
  statusList: ApprovalStatusItem[],
  draft: boolean | undefined,
  currentStatus: number | null,
  currentApprovalStage: number | null | undefined,
  tripStatus: number | undefined
): ApprovalStatusItem[] {
  if (draft) {
    return statusList;
  }

  let currentStatusNumber = 0;
  if (currentStatus === 0) {
    currentStatusNumber = currentApprovalStage != null ? currentApprovalStage - 1 : NaN;
  } else if (currentStatus === 1 || tripStatus === 3) {
    currentStatusNumber = statusList.length - 1;
  }

  return statusList.map((status, index) => ({
    ...status,
    current: index <= currentStatusNumber
  }));
}

class InAdvanceApprovalStatus extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      trip: props.trip,
      department: props.applicant ? props.applicant.department || null : null,
      statusList: [],
      currentStatus: props.currentStatus
    };
  }

  componentDidMount() {
    this.createStatusList();
  }

  createStatusList() {
    let statuses: { name: string; current: boolean; approvers: string[] }[];
    statuses = [];

    if (this.state.trip.inAdvanceApprovals && this.state.trip.inAdvanceApprovals.length > 0) {
      statuses.unshift({ name: '承認済み', current: false, approvers: [] });
    }
    if (this.props.applicant && !this.state.trip.freeeApprovalRequestId) {
      const approvalRequired = this.props.trip.foreignAir
        ? this.props.applicant.foreignApprovalRequired
        : this.props.applicant.domesticApprovalRequired;
      if (approvalRequired) {
        let stages: { [key: string]: ApprovalAuthority[] } = {};
        if (this.state.department && this.state.trip.approvalRouteVersion === 1) {
          stages = _.groupBy(this.state.department.approvalAuthorities, aa => aa.approveStage);
        } else {
          _.forEach(this.state.trip.inAdvanceApprovals, ap => {
            stages[ap.approveStage] = ap.inAdvanceApproverCandidates.map(ca => {
              return new ApprovalAuthority({
                approveStage: ap.approveStage,
                approver: new Approver({ name: ca.name })
              });
            });
          });
        }
        statuses = _.concat(
          _.keys(stages).map(s => {
            const authorities: ApprovalAuthority[] = stages[s];
            return { name: `${s}次承認`, current: false, approvers: authorities.map(a => a.approver?.name || '') };
          }),
          statuses
        );
      }
    }
    this.setCurrentStatus(statuses);
  }

  setCurrentStatus(statusList: { name: string; current: boolean; approvers: string[] }[]) {
    const updatedStatusList = computeApprovalBadgeStates(
      statusList,
      this.state.trip.draft,
      this.state.currentStatus,
      this.state.trip.currentApprovalStage,
      this.state.trip.status
    );
    this.setState({ statusList: updatedStatusList });
  }

  render() {
    try {
      const { trip, statusList, currentStatus } = this.state;
      return (
        <Wrap>
          {!_.isEmpty(trip.isApproveCanceled) ? (
            <Canceled>取消済み</Canceled>
          ) : currentStatus === 2 ? (
            <Canceled>却下済み</Canceled>
          ) : trip.status === 6 ? (
            <Canceled>キャンセル依頼中</Canceled>
          ) : trip.status === 5 ? (
            <Canceled>キャンセル済み</Canceled>
          ) : (
            <StatusItems>
              {statusList.map((s, i) => (
                <StatusItem key={`status_${i}`} current={s.current} first={i === 0}>
                  <StatusIcon>
                    <svg
                      viewBox="0 0 22 22"
                      version="1.1"
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                    >
                      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                        <Circle current={s.current} strokeWidth="6" cx="11" cy="11" r="6" />
                      </g>
                    </svg>
                  </StatusIcon>
                  <StatusText>
                    <span>{s.name}</span>
                    {s.approvers.length > 0 && (
                      <>
                        <span>：</span>
                        <span data-wovn-ignore>{s.approvers.map((n, j) => `${j > 0 ? ', ' : ''}${n}`)}</span>
                      </>
                    )}
                  </StatusText>
                </StatusItem>
              ))}
            </StatusItems>
          )}
        </Wrap>
      );
    } catch (e) {
      reportError(e);
      return null;
    }
  }
}

const Wrap = styled.div`
  font-size: 11px;

  ${media.sp`
    font-size: 10px;
  `}
`;

const Canceled = styled.p`
  color: ${props => props.theme.redColor};
`;

const StatusItems = styled.div`
  margin-top: -12px;
  padding-left: 3px;
`;

const StatusItem = styled.div<{ current: boolean; first: boolean }>`
  position: relative;
  z-index: 0;
  height: 20px;
  margin-bottom: 6px;
  border-left: solid 3px ${props => props.theme.grayTextColor};

  ${props =>
    props.current &&
    `
    border-color: ${props.theme.linkColor};
  `}
  ${props =>
    props.first &&
    `
    border-color: transparent;
  `}
  ${media.sp`
    margin-bottom: 6px;
  `}
`;

const StatusIcon = styled.div`
  position: absolute;
  bottom: -6px;
  left: -7px;
  z-index: 10;
  width: 11px;
  height: 11px;
`;

const Circle = styled.circle<{ current: boolean }>`
  stroke: ${props => props.theme.grayTextColor};

  ${props =>
    props.current &&
    `
    stroke: ${props.theme.linkColor};
  `}
`;

const StatusText = styled.div`
  position: absolute;
  bottom: -10px;
  left: 10px;
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${media.sp`
    bottom: -8px;
  `}
`;

export default InAdvanceApprovalStatus;
